import './Player.css';

import React, {useState, useEffect, useRef} from 'react';
import { PlayerContext } from './PlayerContext.js';
import { StickerContext } from './StickerContext.js';
import { getFileNameFromUrl, getWindowDimensions, getFileFormat, isIOS, printObj, checkMobile, getTextWidth, addOpacityToHexColor, log, logZenchefIframeMessage, sendZenchefPixelEvents, isZenchefBookingCreatedEvent } from './Utils.js';
import StoryVideo, {checkVideoRunning, getStoryDimensions} from './StoryVideo';

import SideMenu from './side_menu/SideMenu';

import { collection, getDocs, getFirestore, onSnapshot, or, query, doc, getDoc, where, setDoc, increment, updateDoc, addDoc, deleteField } from "firebase/firestore";
import { getInstallations, getId} from "@firebase/installations";

import {app, setWebFallback, analyticsManager, webFallback, testedGroup} from '../index';
import StickerInside from './overlay/stickers/StickerInside';
import FullScreenMediaPlayer from './overlay/stickers/FullScreenMediaPlayer';
import StickerGallery, {menuType} from './overlay/stickers/StickerGallery';
import {browserLocales, openFullscreen, closeFullscreen, getDemoPlaceDetails, isCafeUprealityDemoStoryId, isFromDemoFeedQuery, isPlayerEmbeddedInFrame, shouldDisablePlayerFullscreen, translatePlaceType} from './Utils';
import AppAnalyticsManager from './analytics/AppAnalyticsManager';
import { useParams } from "react-router-dom";
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import * as Sentry from "@sentry/react";
import { useAnimate } from 'framer-motion';
import RestaurantInfos from './RestaurantInfos.js';
import ChainRestaurantPlaceDialog from './ChainRestaurantPlaceDialog.js';
import { applySelectedChainRestaurantToStory, findChainRestaurantByPosId, getSharedPosIdFromUrl, hasChainRestaurants } from './chainRestaurantUtils.js';
import { fetchPlaceDetails, getCachedPlaceDetails, getPlayerPlaceDetailsLanguage } from './placeDetailsCache.js';
import { loadMenuFonts } from './menuFontLoader.js';
import PixelConsentOverlay, { getStoredPixelConsent, setStoredPixelConsent, PIXEL_CONSENT_REOPEN_EVENT } from './PixelConsentOverlay.js';
import { installStoryPixels, hasInstallablePixel, getPixelsToInstall, grantPixelConsent, revokePixelConsent, isDirectVisit } from './Utils.js';

// to have the var up-to-date when the setTimeout function occurs
var storyClicked1Time = false;
export function setStoryClicked1Time(value){
  storyClicked1Time = value;
}

var audioFromBgVideoOn = false;
export function setAudioFromBgVideoOn(on){
  audioFromBgVideoOn = on;
}

/** Réf. téléphone HP / DemoFeed (scale 1) → sizeMultiplier 0.7 */
const PLAYER_MINI_REF_WIDTH = 244;
const PLAYER_MINI_REF_HEIGHT = 540;
const PLAYER_MINI_BASE_MULTIPLIER = 0.7;

function computePlayerSizeMultiplier(dims, mini, isTabletOrMobile) {
  const narrowEmbedded = isPlayerEmbeddedInFrame() && dims != null && dims.width > 0 && dims.width < 400;
  if (((mini != null && mini) || narrowEmbedded) && dims != null && (dims.height || dims.width)) {
    const widthScale = dims.width > 0 ? dims.width / PLAYER_MINI_REF_WIDTH : 1;
    const heightScale = dims.height > 0 ? dims.height / PLAYER_MINI_REF_HEIGHT : 1;
    const scale = Math.min(widthScale, heightScale);
    return Math.max(0.55, PLAYER_MINI_BASE_MULTIPLIER * scale);
  }
  if (isTabletOrMobile && dims != null && dims.width >= 400) {
    return 1.1;
  }
  return 1.0;
}

function resolvePlayerFromDemo(props, routeStoryId) {
  if (props.fromDemo) {
    return props.fromDemo;
  }
  if (isFromDemoFeedQuery()) {
    return 'demo-feed';
  }
  return undefined;
}

function normalizeCafeDemoStoryId(storyId) {
  if (storyId != null && isCafeUprealityDemoStoryId(storyId)) {
    return String(storyId).toLowerCase();
  }
  return storyId;
}

function Player(props) {
  const storyIdParams = useParams();
  const fromDemo = resolvePlayerFromDemo(props, storyIdParams.storyId);
  const initialRouteStoryId = props.storyId != null ? props.storyId : storyIdParams.storyId;
  const normalizedInitialStoryId = normalizeCafeDemoStoryId(initialRouteStoryId);

  const [isLoaded, setIsLoaded] = useState(false);
  const [story, setStory] = useState(null);
  const [menuTitleFont, setMenuTitleFont] = useState(null);
  const [menuTextFont, setMenuTextFont] = useState(null);
  const [menuDescFont, setMenuDescFont] = useState(null);
  const [menuTitleFontFallback, setMenuTitleFontFallback] = useState(false);
  const [menuTextFontFallback, setMenuTextFontFallback] = useState(false);
  const [menuDescFontFallback, setMenuDescFontFallback] = useState(false);
  const [menuFontsLoading, setMenuFontsLoading] = useState(false);
  const [storyId, setStoryId] = useState((props.fromMaker || fromDemo) ? normalizedInitialStoryId : null);  
  const [placeDetails, setPlaceDetails] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [menuStickerDimensions, setMenuStickerDimensions] = useState({width: 0, height: 0});
  const [insideStickerDimensions, setInsideStickerDimensions] = useState({width: 0, height: 0});
  const [stickersOutOfScreen, setStickersOutOfScreen] = useState(false);  
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isMobile = useMediaQuery({ query: '(max-width: 540px)' });
  const playerOnDesktop = ! isTabletOrMobile && ! props.fromMaker && !fromDemo;
  const [windowDimensions, setWindowDimensions] = useState(! playerOnDesktop ? props.windowDimensions : {width: 360, height: Math.min(720, props.windowDimensions.height - 72)});
  const [reviewsAlreadyDisplayed, setReviewsAlreadyDisplayed] = useState(false);
  const [placeV2alreadyCalled, setPlaceV2alreadyCalled] = useState(false);
  
  const [sizeMultiplier, setSizeMultiplier] = useState(() =>
    computePlayerSizeMultiplier(
      !playerOnDesktop ? props.windowDimensions : { width: 360, height: Math.min(720, props.windowDimensions.height - 72) },
      props.mini,
      isTabletOrMobile
    )
  );
  
  const [isFullScreenOpened, setisFullScreenOpened] = useState(false);
  const [mediaFullScreenOpened, setMediaFullScreenOpened] = useState(null);
     
  const [activeFeature, setActiveFeature] = useState(null);
  const pendingFeatureAfterPlaceRef = useRef(null);
  const chainPickerPreserveFeatureRef = useRef(null);
  const storyUsageIncrementedRef = useRef(false);
  const chainRestaurantFromUrlAppliedRef = useRef(false);
  const chainDefaultMenuIdRef = useRef(undefined);
  const CHAIN_PLACE_FEATURES = new Set(['hours', 'location', 'book', 'reviews', 'chat', 'menu']);
  const CHAIN_INTERMEDIATE_FEATURE = 'intermediate';
  const [playInteractiveAnimation, setPlayInteractiveAnimation] = useState(fromDemo === "top");  
  const [stickerOpeningPlayed, setStickerOpeningPlayed] = useState(fromDemo);

  const storyDimensions = getStoryDimensions(story, windowDimensions);  

  // initialized after story is loaded, can be null while the story isnt
  const [playMode, setPlayMode] = useState(null);
  const idPrefix = fromDemo ? (fromDemo +"-") : "";
  const playerContainerId = fromDemo
    ? `player-container-fromdemo-${String(fromDemo)}`
    : 'player-container';

  function getElInPlayerContainer(elementId) {
    const root = document.getElementById(playerContainerId);
    if (root == null) return null;
    return root.querySelector(`#${elementId}`);
  }

  const appAnalyticsManager = useRef(null);

  const [snackSwipeAlreadyDisplayed, setSnackSwipeAlreadyDisplayed] = useState(false);  
  const [menuStickerClicked, setMenuStickerClicked] = useState(false);
  const [insideStickerClicked, setInsideStickerClicked] = useState(false);
  const [shareButtonAlreadyHighlighted, setShareButtonAlreadyHighlighted] = useState(false);

  const storyRef = React.useRef(null);

  const { t } = useTranslation();  

  const [scope, animate] = useAnimate();

  const audioRef = React.createRef();
  const [playAudio, setPlayAudio] = useState(false);
  const [audioAlreadyDisabled, setAudioAlreadyDisabled] = useState(false);

  const embeddedPlayerWidth = props.windowDimensions?.width;
  const embeddedPlayerHeight = props.windowDimensions?.height;

  // fromDemo / fromMaker: sync parent metrics (HP compactLaptop, etc.) without remounting
  useEffect(() => {
    if ((!fromDemo) || embeddedPlayerWidth == null || embeddedPlayerHeight == null) {
      return;
    }
    const next = { width: embeddedPlayerWidth, height: embeddedPlayerHeight };
    setWindowDimensions((prev) => {
      if (prev && prev.width === next.width && prev.height === next.height) {
        return prev;
      }
      return next;
    });
    setSizeMultiplier(computePlayerSizeMultiplier(next, props.mini, isTabletOrMobile));
  }, [fromDemo, embeddedPlayerWidth, embeddedPlayerHeight, props.mini, isTabletOrMobile]);

  useEffect(() => {    
    if (audioRef.current != null && story != null /*&& !fromDemo*/ && story.musicUrl){
      if (playAudio && (audioRef.current.paused)){        
        audioRef.current.play();        
      }
      else if (! playAudio && ! audioRef.current.paused){
        audioRef.current.pause();
      }      
    }    
  }, [playAudio]);

  useEffect(() => {
    if (activeFeature != null){
      setStickerOpeningPlayed(true);
    }
    else {
      if (story){
        checkVideoRunning(story, idPrefix);
      }
    }
  }, [activeFeature]);

  const hasPixel = hasInstallablePixel(story);
  const installablePixels = getPixelsToInstall(story);
  const pixelModeActive = playMode === "play" || playMode === "review";
  const getConsentStoryId = () => {
    const currentStoryId = storyId || props.storyId;
    if (!currentStoryId || !pixelModeActive) {
      return null;
    }
    return currentStoryId;
  };
  const zenchefBookingCreatedFiredRef = useRef(false);
  const pixelConsentInitialShownLoggedRef = useRef(false);
  useEffect(() => {
    if (!pixelModeActive || !hasPixel){           
      return undefined;
    }    

    const handleZenchefIframeMessage = (event) => {
      const context = {
        storyId: storyId || props.storyId || null,
        playMode,
        activeFeature,
      };
      logZenchefIframeMessage(event, context);

      // Pas de consentement accepté : on n'envoie aucun event pixel.
      if (getStoredPixelConsent() !== 'accepted'){
        return;
      }

      // Éviter d'envoyer plusieurs fois la conversion (postMessage rejoué).
      if (isZenchefBookingCreatedEvent(event)){
        if (zenchefBookingCreatedFiredRef.current){
          return;
        }
        zenchefBookingCreatedFiredRef.current = true;
      }

      sendZenchefPixelEvents(event, context);
    };

    window.addEventListener('message', handleZenchefIframeMessage);
    return () => window.removeEventListener('message', handleZenchefIframeMessage);
  }, [storyId, props.storyId, playMode, activeFeature, hasPixel, pixelModeActive]);

  const [pixelConsentNeeded, setPixelConsentNeeded] = useState(false);
  const [pixelConsentIsReopen, setPixelConsentIsReopen] = useState(false);
  useEffect(() => {
    if (!pixelModeActive || !hasPixel) {
      setPixelConsentNeeded(false);
      return;
    }
    const consent = getStoredPixelConsent();
    if (consent === 'accepted') {
      // Consentement déjà donné : on installe les pixels.
      pixelConsentInitialShownLoggedRef.current = false;
      installStoryPixels(story);
      grantPixelConsent();
      setPixelConsentNeeded(false);
    } else if (consent === 'refused') {
      pixelConsentInitialShownLoggedRef.current = false;
      setPixelConsentNeeded(false);
    } else if (isDirectVisit()) {
      // Visite directe (ni fbclid ni ttclid) : consentement auto, NON persisté
      // dans localStorage (repli mémoire uniquement), sans afficher l'overlay.
      setStoredPixelConsent('accepted', { persist: false });
      installStoryPixels(story);
      grantPixelConsent();
      const consentStoryId = getConsentStoryId();
      if (consentStoryId) {
        analyticsManager.logPixelConsentTag(consentStoryId, playMode, 'direct', true);
      }
      setPixelConsentNeeded(false);
    } else {
      setPixelConsentIsReopen(false);
      setPixelConsentNeeded(true);
      if (!pixelConsentInitialShownLoggedRef.current) {
        pixelConsentInitialShownLoggedRef.current = true;
        const consentStoryId = getConsentStoryId();
        if (consentStoryId) {
          analyticsManager.logOpenPopupTag(consentStoryId, 'pixel_consent', true, playMode);
        }
      }
    }
  }, [playMode, hasPixel, story, pixelModeActive]);

  useEffect(() => {
    const reopenHandler = () => {
      if (pixelModeActive && hasPixel) {
        setPixelConsentIsReopen(true);
        setPixelConsentNeeded(true);
        const consentStoryId = getConsentStoryId();
        if (consentStoryId) {
          analyticsManager.logOpenPopupTag(consentStoryId, 'pixel_consent_manage', true, playMode);
        }
      }
    };
    window.addEventListener(PIXEL_CONSENT_REOPEN_EVENT, reopenHandler);
    return () => window.removeEventListener(PIXEL_CONSENT_REOPEN_EVENT, reopenHandler);
  }, [playMode, hasPixel, pixelModeActive]);

  const acceptPixelConsent = () => {
    const consentType = pixelConsentIsReopen ? 'manage' : 'initial';
    const consentStoryId = getConsentStoryId();
    if (consentStoryId) {
      analyticsManager.logPixelConsentTag(consentStoryId, playMode, consentType, true);
    }
    setStoredPixelConsent('accepted');
    setPixelConsentIsReopen(false);
    setPixelConsentNeeded(false);
    installStoryPixels(story);
    // Réautorise aussi le consentement natif si les pixels étaient déjà installés.
    grantPixelConsent();
  };
  const refusePixelConsent = () => {
    const consentType = pixelConsentIsReopen ? 'manage' : 'initial';
    const consentStoryId = getConsentStoryId();
    if (consentStoryId) {
      analyticsManager.logPixelConsentTag(consentStoryId, playMode, consentType, false);
    }
    setStoredPixelConsent('refused');
    setPixelConsentIsReopen(false);
    setPixelConsentNeeded(false);
    // Coupe la collecte côté SDK (y compris automatique), en plus de notre garde.
    revokePixelConsent();
  };
  const closePixelConsentOverlay = () => {
    const consentStoryId = getConsentStoryId();
    if (consentStoryId) {
      analyticsManager.logOpenPopupTag(consentStoryId, 'pixel_consent_manage', false, playMode);
    }
    setPixelConsentIsReopen(false);
    setPixelConsentNeeded(false);
  };

  const [retryBgVideo, setRetryBgVideo] = useState(true);
  useEffect(() => {    
    if (story && retryBgVideo){
      setRetryBgVideo(false);      
      setTimeout(()=>reloadVideoIfNoOverlays(story, idPrefix), 5000);
      setTimeout(()=>reloadVideoIfNoOverlays(story, idPrefix), 10000);      
    }

    function reloadVideoIfNoOverlays(story, idPrefix){
      const videoWithoutOverlays = document.getElementById("primary-btn-share") != null;
      if (videoWithoutOverlays){
        checkVideoRunning(story, idPrefix);
      }
    }
  }, [story, retryBgVideo]);

  if (! props.fromMaker && ! fromDemo && playerOnDesktop){
    //document.getElementsByTagName('body')[0].className = 'PatternBackground';
  }
  else if (! props.fromMaker && (fromDemo === 'demo-feed' || shouldDisablePlayerFullscreen())){
    document.getElementsByTagName('body')[0].className = 'BlackBackground';
  }
  else if (! props.fromMaker && ! fromDemo){
    if (playerOnDesktop || isTabletOrMobile && !isMobile){
      document.getElementsByTagName('body')[0].className = 'WhiteBackground';
    }
    else {
      document.getElementsByTagName('body')[0].className = 'BlackBackground';
    }
  }

  const fetchStory = async()=>{
    let unsubscribe = null;  
    let unsubscribePostPublish = null;

    let storyDocument = null;
    let storyDocumentData = null;
    const db = getFirestore();
    
    if (props.fromMaker || fromDemo){
      const routeStoryKey = storyId || storyIdParams.storyId || props.storyId;
      if (routeStoryKey) {
        storyDocument = await getDoc(doc(db, "stories", routeStoryKey));
        if (!storyDocument.exists() || storyDocument.data() == undefined) {
          storyDocument = null;
          if (fromDemo) {
            const aliasLowerCase = String(routeStoryKey).toLowerCase();
            const q = query(
              collection(db, "stories"),
              or(where("aliasLowerCase", "==", aliasLowerCase), where("previousAliasLowerCase", "==", aliasLowerCase)),
            );
            const querySnapshot = await getDocs(q);
            const storyDocs = querySnapshot.docs.filter((d) => d.data().baseStory === true || d.data().baseStory == null);
            if (storyDocs.length > 0) {
              storyDocument = storyDocs[0];
              setStoryId(storyDocument.id);
            }
          }
        }
      }
      if (storyDocument != null) {
        storyDocumentData = storyDocument.data();
      }
    }
    else {    
      if (process.env.NODE_ENV === 'development'){
        if (props.storyId){
          const url = new URL(window.location);
          url.searchParams.delete("subdomainredirect");
          window.history.replaceState({}, '', url);
        }
        const storiesRef = collection(db, "stories");
        const aliasLowerCase = storyIdParams.storyId ? storyIdParams.storyId.toLowerCase() : props.storyId.toLowerCase();
        const q = query(storiesRef, or(where("aliasLowerCase", "==", aliasLowerCase), where("previousAliasLowerCase", "==", aliasLowerCase)));
        const querySnapshot = await getDocs(q);
        const storyDocs = querySnapshot.docs.filter((doc)=>doc.data().baseStory === true || doc.data().baseStory == null);
        storyDocs.forEach((doc) => {
          storyDocument = doc;
        });   
        if (storyDocument != null){      
          setStoryId(storyDocument.id);
          storyDocumentData = storyDocument.data();
        }
      }
      else { 
        try {                
          storyDocument = JSON.parse(document.getElementById("story").innerHTML);          
          // TODO restore
          //document.getElementById("story").remove();        
          //
        }
        catch (e){                        
          Sentry.captureMessage("Story not found while loading from html: " + e.message);
        }
        
        storyDocumentData = storyDocument;
        setStoryId(storyDocument.id);
      }
    }

    // TODO handle back press for all sub states
    // TODO everything material-ui
    // TODO windowDimensions and dp px

    if (storyDocument != null && storyDocument.id) {       
      if (! props.fromMaker && ! fromDemo && !isMobile){        
        document.getElementsByTagName('body')[0].style.backgroundColor = addOpacityToHexColor(storyDocumentData.primaryColor, 0.2);
      }

      setIsLoaded(true);            
      setStory(storyDocumentData);
      let fallback = null;
      if (storyDocumentData.website){
        fallback = storyDocumentData.website;
      }
      else if (storyDocumentData.posName && storyDocumentData.posId) {
        fallback = "https://www.google.com/maps/search/?api=1&query=" + encodeURI(storyDocumentData.posName) + "&query_place_id=" + storyDocumentData.posId;
      }
      setWebFallback(fallback);

      if (!props.fromMaker && !fromDemo){        
        // check if the user is banned for the story
        if (storyDocumentData.bannedUsers && storyDocumentData.ipHash != null && storyDocumentData.ipHash !== "" && storyDocumentData.serverDate != null) {
          const bannedUsers = JSON.parse(storyDocumentData.bannedUsers);          
          let bannedUser = null;
          for (const user of bannedUsers) {            
            if (user.ipHash === storyDocumentData.ipHash && 
                user.date > storyDocumentData.serverDate - 1000 * 60 * 60 * 6) { // 6 hours ban
              bannedUser = user;
              break;
            }
          }
          if (bannedUser) {
            window.location.replace(fallback);
            Sentry.captureMessage("Story banned for ipHash: " + storyDocumentData.ipHash + " (ban expiré le: " + new Date(bannedUser.date + 1000 * 60 * 60 * 6).toISOString() + ")");
            return;
          }
        }
      }
      //
      
      // TODO limit to maker only or also used in player (eg with stats) ?
      unsubscribe = onSnapshot(doc(db, "stories", storyDocument.id), (fbDoc) => {              
        if (props.fromMaker && storyRef.current != null && storyRef.current.baseStoryId != null && fbDoc.data() == undefined && unsubscribePostPublish == null){
          unsubscribePostPublish = onSnapshot(doc(db, "stories", storyRef.current.baseStoryId), (doc) => {            
            if (doc.data() != undefined){              
              // update with attributes that can change following resizing and thumbnail generation post publish              
              storyRef.current.width = doc.data().width;
              storyRef.current.height = doc.data().height;
              storyRef.current.offsetValue = doc.data().offsetValue;
              storyRef.current.imageRef = doc.data().imageRef;
              storyRef.current.videoRef = doc.data().videoRef;
              storyRef.current.mediasRef = doc.data().mediasRef;
              storyRef.current.tourMediasRef = doc.data().tourMediasRef;
              storyRef.current.dayMenuFile = doc.data().dayMenuFile;
              setStory(storyRef.current);
            }
          });
        }     
        if (fbDoc.data() == undefined || !props.fromMaker) return;        
        // case where the story is restarted from the maker     
        const updateStory = fbDoc.data();
        setStory(updateStory);        
      });      
  
      if (storyDocumentData != undefined){
        if (isCafeUprealityDemoStoryId(storyDocument.id)){
          // Load Pacifico font for cafeupreality and cafeuprealityad stories            
      
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://fonts.googleapis.com/css2?family=Pacifico:wght@400&display=block';
          document.head.appendChild(link);
          setMenuTitleFont('Pacifico');        

          setPlaceDetails(getDemoPlaceDetails());
        }
      }
      else {
        Sentry.captureMessage("storyDocumentData undefined");
      }    
      
      if (props.fromMaker && !storyDocumentData.menuId){
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Pacifico:wght@400&display=block';
        document.head.appendChild(link);
        setMenuTitleFont('Pacifico');  
      }

      if (! props.fromMaker && ! fromDemo && storyDocumentData.ipHash != null && storyDocumentData.ipHash !== "" && storyDocumentData.serverDate != null){        
        updateBannedUsers(storyDocument.id, storyDocumentData);
      }

      
    } else {
      // TODO specific error screen
      setIsLoaded(false);
      window.location.replace('/');
      return;
    }    
    
    return () => {
      if (unsubscribe != null){
        unsubscribe();
      }
      if (unsubscribePostPublish != null){
        unsubscribePostPublish();
      }      
    }
  }  

  async function updateBannedUsers(storyId, story){
    const db = getFirestore();
    const storyReadsRef = doc(db, "story_reads", story.ipHash + "_" + storyId);
    const storyReads = await getDoc(storyReadsRef);
    const storyRef = doc(db, "stories", storyId);

    if (! storyReads.exists()){
      await setDoc(storyReadsRef, {countStartingDate: story.serverDate, viewCount: 1});
    }
    else {
      if (storyReads.data().countStartingDate < story.serverDate - 1000 * 60 * 60){
        await updateDoc(storyReadsRef, {countStartingDate: story.serverDate, viewCount: 1});
      }
      else {        
        if (storyReads.data().viewCount < 30){
          await updateDoc(storyReadsRef, {viewCount: increment(1)});
        }
        // ban user if view count is greater than 30 on 1 hour
        else {
          const bannedUsers = story.bannedUsers ? JSON.parse(story.bannedUsers) : [];
          bannedUsers.push({date: story.serverDate, ipHash: story.ipHash});
          await updateDoc(storyRef, {bannedUsers: JSON.stringify(bannedUsers)});
          await addDoc(collection(db, "mail"), {
            to: "jacques@upreality.store",
            from: "Jacques - UpReality <jacques@upreality.store>",
            message: {
                subject: "BAN IP",                    
                text: "Un utilisateur a été banni pour le hash IP " + story.ipHash + " sur la story " + storyId,
            },
        });
        }
      }      
    }
    // remove expired bans
    if (story && story.bannedUsers) {      
       const filteredBannedUsers = [];
       const bannedUsers = JSON.parse(story.bannedUsers);
       for (const bannedUser of bannedUsers) {        
        if (bannedUser.date > story.serverDate - 1000 * 60 * 60 * 6) {
          filteredBannedUsers.push(bannedUser);
        }
    }
            
    if (filteredBannedUsers.length === 0) {
      await updateDoc(storyRef, {
           bannedUsers: deleteField()
         });
      } else {
         await updateDoc(storyRef, {
           bannedUsers: JSON.stringify(filteredBannedUsers)
         });
       }
    }
    
  }

  useEffect(() => {
    // to have a story version usable in firebase callbacks
    if (props.fromMaker && story != null){
      storyRef.current = story;
    }
  }, [story]);

  useEffect(() => {
    // to have a story version usable in firebase callbacks
    if (story && !story.premium && !fromDemo && !isCafeUprealityDemoStoryId(storyId)){      
      const placeDetailsByManualInput = JSON.parse(story.placeDetails ? story.placeDetails : "{}");
      setPlaceDetails(placeDetailsByManualInput);
    }
  }, [story, storyId, fromDemo]);

  useEffect(() => {
    if (!story || chainRestaurantFromUrlAppliedRef.current || props.fromMaker || fromDemo) {
      return;
    }
    if (!hasChainRestaurants(story)) {
      return;
    }

    const posIdFromUrl = getSharedPosIdFromUrl();
    if (!posIdFromUrl || story.posId === posIdFromUrl) {
      if (posIdFromUrl) {
        chainRestaurantFromUrlAppliedRef.current = true;
      }
      return;
    }

    const restaurant = findChainRestaurantByPosId(story.chainRestaurants, posIdFromUrl);
    if (!restaurant) {
      chainRestaurantFromUrlAppliedRef.current = true;
      return;
    }

    chainRestaurantFromUrlAppliedRef.current = true;
    const updatedStory = applySelectedChainRestaurantToStory(
      story,
      restaurant,
      chainDefaultMenuIdRef.current ?? null,
    );
    setStory(updatedStory);

    if (!isCafeUprealityDemoStoryId(storyId)) {
      if (updatedStory.premium) {
        setPlaceDetails(null);
        setPlaceV2alreadyCalled(true);
        getPlaceDetails(updatedStory, storyId);
      } else {
        const placeDetailsByManualInput = JSON.parse(updatedStory.placeDetails ? updatedStory.placeDetails : "{}");
        setPlaceDetails(placeDetailsByManualInput);
      }
    }
  }, [story, storyId, props.fromMaker, fromDemo]);

  useEffect(() => {
    chainDefaultMenuIdRef.current = undefined;
  }, [storyId]);

  useEffect(() => {
    if (!story || !hasChainRestaurants(story)) {
      return;
    }
    if (chainDefaultMenuIdRef.current === undefined) {
      chainDefaultMenuIdRef.current = story.menuId ?? null;
    }
  }, [story]);

  useEffect(() => {
    if (!story?.menuId || story.menuId === "0") {
      setMenuTitleFont(null);
      setMenuTextFont(null);
      setMenuDescFont(null);
      setMenuTitleFontFallback(false);
      setMenuTextFontFallback(false);
      setMenuDescFontFallback(false);
      setMenuFontsLoading(false);
      return;
    }

    let cancelled = false;
    const setIfActive = (setter) => (value) => {
      if (!cancelled) {
        setter(value);
      }
    };

    loadMenuFonts(story.menuId, {
      setMenuTitleFont: setIfActive(setMenuTitleFont),
      setMenuTextFont: setIfActive(setMenuTextFont),
      setMenuDescFont: setIfActive(setMenuDescFont),
      setMenuTitleFontFallback: setIfActive(setMenuTitleFontFallback),
      setMenuTextFontFallback: setIfActive(setMenuTextFontFallback),
      setMenuDescFontFallback: setIfActive(setMenuDescFontFallback),
      setMenuFontsLoading: setIfActive(setMenuFontsLoading),
    });

    return () => {
      cancelled = true;
    };
  }, [story?.menuId]);

  useEffect(() => {
    if (!story?.posId && !hasChainRestaurants(story) || storyUsageIncrementedRef.current || isCafeUprealityDemoStoryId(storyId)) {
      return;
    }
    if (fromDemo || props.fromMaker || getAuth().currentUser != null) {
      return;
    }
    if (!story.premium || (story.trialEnd && story.trialEnd >= new Date().getTime())) {
      return;
    }
    storyUsageIncrementedRef.current = true;
    incrementStoryUsage(storyId, story.uid);
  }, [story?.posId, story?.premium, story?.trialEnd, story?.uid, storyId, fromDemo, props.fromMaker]);

  useEffect(() => {
    const isDemo = isCafeUprealityDemoStoryId(storyId);
    if (story && placeDetails && !placeDetails.reviews && ! reviewsAlreadyDisplayed && activeFeature === "reviews" || story && !placeDetails && ! placeV2alreadyCalled && (activeFeature === "reviews" || activeFeature === "hours")){      
      if (hasChainRestaurants(story) && !story.posId && !isDemo) {
        return;
      }
      if (isDemo) {
        setPlaceDetails(getDemoPlaceDetails());
        return;
      }
      setPlaceV2alreadyCalled(true);      
      
      getPlaceDetails(story, storyId);
    }
  }, [reviewsAlreadyDisplayed, activeFeature, story, placeDetails, storyId]);

  useEffect(() => {
    if (!story?.posId || !pendingFeatureAfterPlaceRef.current) {
      return;
    }
    const feature = pendingFeatureAfterPlaceRef.current;
    if (feature === 'location') {
      return;
    }
    pendingFeatureAfterPlaceRef.current = null;
    setActiveFeature(feature);
  }, [story?.posId]);

  useEffect(() => {
    if (pendingFeatureAfterPlaceRef.current !== 'location' || !story?.posId) {
      return;
    }
    pendingFeatureAfterPlaceRef.current = null;
    analyticsManager.logCheckItineraryTag(storyId, playMode);
    if (appAnalyticsManager.current) {
      appAnalyticsManager.current.clickedLocation();
    }
    pauseVideo();
    openFullscreen();
    setActiveFeature('location');
  }, [story?.mapsSearchQuery, story?.posId, story, storyId, playMode]);

  async function getPlaceDetails(storyData, storyIdParam){
    if (isCafeUprealityDemoStoryId(storyIdParam)) {
      setPlaceDetails(getDemoPlaceDetails());
      return;
    }

    const posId = storyData.posId;
    if (posId == null) {
      return;
    }

    if (!storyData.premium) {
      const placeDetailsByManualInput = JSON.parse(storyData.placeDetails ? storyData.placeDetails : "{}");
      setPlaceDetails(placeDetailsByManualInput);
      return;
    }

    const language = getPlayerPlaceDetailsLanguage();
    const cached = getCachedPlaceDetails(posId, language);
    if (cached) {
      setPlaceDetails(cached);
      return;
    }

    function redirectIfPlaceKO(){
      if (webFallback){
        window.location.replace(webFallback);
      }
    }

    function applyPlaceDetails(data) {
      if (data != null) {
        setPlaceDetails(data);
      } else {
        Sentry.captureMessage("Place detail by url error, null response");
      }
    }

    fetchPlaceDetails(posId, language)
      .then(applyPlaceDetails)
      .catch((error) => {
        Sentry.captureMessage("Place first call by url failed: " + printObj(error));
        setTimeout(() => fetchPlaceDetails(posId, language)
          .then(applyPlaceDetails)
          .catch((e) => {
            Sentry.captureMessage("Place second call by url failed: " + printObj(e));
            redirectIfPlaceKO();
          }), 2000);
      });
  }

  async function incrementStoryUsage(storyId, uid) {
    try {
      const db = getFirestore();
      const storyUsageRef = doc(db, "story_usages", storyId + "_" + uid);
      await setDoc(storyUsageRef, {
        usageCount: increment(1),
        lastUpdated: new Date().getTime(),
        storyId: storyId,
        uid: uid
      }, { merge: true });
    } catch (error) {
      Sentry.captureMessage("Error incrementing story usage: " + error.message);
    }
  }

  function closeChainPlaceDialog() {
    pendingFeatureAfterPlaceRef.current = null;
    chainPickerPreserveFeatureRef.current = null;
    setActiveFeature(null);
  }

  function openChainRestaurantPicker() {
    if (!hasChainRestaurants(story)) {
      return;
    }
    if (activeFeature === 'hours' || activeFeature === 'reviews') {
      chainPickerPreserveFeatureRef.current = activeFeature;
    } else {
      chainPickerPreserveFeatureRef.current = null;
    }
    pauseVideo();
    openFullscreen();
    pendingFeatureAfterPlaceRef.current = null;
    setActiveFeature(CHAIN_INTERMEDIATE_FEATURE);
  }

  function handleChainPlaceSelected(selectedRestaurant) {
    if (!selectedRestaurant?.posId || !story) {
      return;
    }
    const isDemo = isCafeUprealityDemoStoryId(storyId);
    const previousPosId = story?.posId;
    const hadPlaceDetails = placeDetails != null;
    const preserveFeature = chainPickerPreserveFeatureRef.current;
    chainPickerPreserveFeatureRef.current = null;

    const updatedStory = applySelectedChainRestaurantToStory(
      story,
      selectedRestaurant,
      chainDefaultMenuIdRef.current ?? null,
    );
    const restaurantChanged = previousPosId != null && previousPosId !== selectedRestaurant.posId;
    const shouldRefetchPlace = !isDemo && restaurantChanged && (hadPlaceDetails || preserveFeature === 'hours' || preserveFeature === 'reviews');

    setStory(updatedStory);
    if (!isDemo) {
      const language = getPlayerPlaceDetailsLanguage();
      const cachedPlaceDetails = getCachedPlaceDetails(selectedRestaurant.posId, language);
      setPlaceDetails(cachedPlaceDetails);
      setPlaceV2alreadyCalled(cachedPlaceDetails != null);
      setReviewsAlreadyDisplayed(false);
    }

    const pendingFeature = pendingFeatureAfterPlaceRef.current;
    let nextFeature = null;
    if (pendingFeature === 'location') {
      nextFeature = null;
    } else if (pendingFeature) {
      pendingFeatureAfterPlaceRef.current = null;
      nextFeature = pendingFeature;
    } else if (preserveFeature) {
      nextFeature = preserveFeature;
    }

    setActiveFeature(nextFeature);

    if (isDemo && (nextFeature === 'hours' || nextFeature === 'reviews')) {
      setPlaceDetails(getDemoPlaceDetails());
    } else if (shouldRefetchPlace && (nextFeature === 'hours' || nextFeature === 'reviews') && !getCachedPlaceDetails(updatedStory.posId, getPlayerPlaceDetailsLanguage())) {
      setPlaceV2alreadyCalled(true);
      getPlaceDetails(updatedStory, storyId);
    }
  }

  function requestActiveFeature(feature) {
    if (hasChainRestaurants(story) && !story?.posId && feature != null && CHAIN_PLACE_FEATURES.has(feature)) {
      pauseVideo();
      openFullscreen();
      pendingFeatureAfterPlaceRef.current = feature;
      setActiveFeature(CHAIN_INTERMEDIATE_FEATURE);
      return false;
    }
    setActiveFeature(feature);
    return true;
  }

  function propagateFocusChange(videoFocused){      
    checkBgVideoPlaying();
    if (videoFocused){    
      if (activeFeature === CHAIN_INTERMEDIATE_FEATURE) {
        pendingFeatureAfterPlaceRef.current = null;
      }
      else if (activeFeature === "menu"){
        analyticsManager.logOpenMenuTag(storyId, playMode, false);        
      }
      else if (activeFeature === "inside"){
        analyticsManager.logOpenInsideTag(storyId, playMode, false);        
      }
      else if (activeFeature === "hours"){
        analyticsManager.logCheckHoursTag(storyId, playMode, false);
      }
      else if (activeFeature === "reviews"){
        analyticsManager.logCheckReviewsTag(storyId, playMode, false);
      }      
      
      setActiveFeature(null);   
    }
  }        

  const setFullScreen = (url, fullScreen) => {
    if (fullScreen){
      setisFullScreenOpened(true);
      setMediaFullScreenOpened(url);            
    }
    else {
      setisFullScreenOpened(false);      
    }
  };

  const fullScreenMediaFormat = getFileFormat(getFileNameFromUrl(mediaFullScreenOpened));

  // in StoryVideo ??
  function handleResize(tryAgain = true) {    
    const next = getWindowDimensions();
    setWindowDimensions(next);
    setSizeMultiplier(computePlayerSizeMultiplier(next, props.mini, isTabletOrMobile));
    // handle resize not well taken into account by instagram in app browser on ios when scrolling at edges
    if (tryAgain /*&& isInstagramInAppBrowser() && isIOS()*/){      
      setTimeout(()=>handleResize(false), 500);
      setTimeout(()=>handleResize(false), 1000);
      setTimeout(()=>handleResize(false), 2000);
    }
  }

  useEffect(() => {
    if (!shouldDisablePlayerFullscreen()) {
      return undefined;
    }
    closeFullscreen();
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.backgroundColor = '#000';
    document.body.style.overflow = 'hidden';
    document.body.style.backgroundColor = '#000';
    function onFullscreenChange() {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        closeFullscreen();
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.backgroundColor = '';
      document.body.style.overflow = '';
      document.body.style.backgroundColor = '';
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  useEffect(() => {    
    if (! props.fromMaker && ! fromDemo && ! playerOnDesktop){
      window.addEventListener('resize', handleResize);

      setTimeout(()=>handleResize(false), 1000);
      setTimeout(()=>handleResize(false), 2000);
      setTimeout(()=>handleResize(false), 4000); 
    }              

    // HP phone bg can capture taps above the player.
    const pathname = window.location.pathname || "/";
    const isMainHomePath = /^\/(?:fr|en)?\/?$/.test(pathname);
    const isHomePath = isMainHomePath;
    const hpPhoneBgId = fromDemo === "top" ? "hp-phonebg-top" : (fromDemo === "bottom" ? "hp-phonebg-bottom" : null);
    const hpPhoneBgElement = hpPhoneBgId ? document.getElementById(hpPhoneBgId) : null;

    if (isTabletOrMobile && hpPhoneBgElement != null){
      if (isHomePath){
        hpPhoneBgElement.style.pointerEvents = "none";
      }
      else {
        hpPhoneBgElement.addEventListener('click', manageClickOnRoot);
      }
    }
  
    return () => {     
      if (isTabletOrMobile && hpPhoneBgElement != null){
        if (isHomePath){
          hpPhoneBgElement.style.pointerEvents = "";
        }
        else {
          hpPhoneBgElement.removeEventListener('click', manageClickOnRoot);
        }
      }
      if (! props.fromMaker && ! fromDemo && ! playerOnDesktop)
        window.removeEventListener('resize', handleResize);
    };

  }, []);

  function manageClickOnRoot(){        
    if (!props.fromMaker /*&& !fromDemo*/ && !playerOnDesktop){             
      if (!audioAlreadyDisabled && !playAudio){        
        const ambiantPlayer = document.getElementById(idPrefix + "audio-player");
        const volumeBtnOn = document.getElementById(idPrefix + "story-volume-btn-on");
        const volumeBtnOff = document.getElementById(idPrefix + "story-volume-btn-off");
        if (ambiantPlayer != null && volumeBtnOn != null && volumeBtnOff != null){ 
          if (ambiantPlayer.paused){
            try {
              ambiantPlayer.play();
            }
            catch (e){}
          }
          volumeBtnOff.style.visibility = "visible";
          volumeBtnOn.style.visibility = "hidden";                    
        }
        setPlayAudio(true);
      }
      if (!fromDemo && !shouldDisablePlayerFullscreen()) {
        openFullscreen();
      }
    }

    setStoryClicked1Time(true);
    setPlayInteractiveAnimation(false);    
    if (playInteractiveAnimation){
      // on demo play sticker animation after ending the touch to play one
      setStickerOpeningPlayed(false);
    }
    if (document.getElementsByClassName("MediasGalleryContainer") == null || document.getElementsByClassName("MediasGalleryContainer").length === 0){
      checkBgVideoPlaying();
    }            
  }
  
  // can be delayed and not finished on the first appAnalytics event (rare) -> if on every call
  useEffect(() => {    
    if (story == null || storyId == null || appAnalyticsManager.current != null) return;

    getAuth().authStateReady()
      .then(()=>initAnalytics(true))
      .catch((e)=>initAnalytics(false));
    
    async function initAnalytics(checkAuth){
      let installId = await getId(getInstallations(app)).catch((e)=>null); // Échec normal dans navigateurs in-app
  
      if (installId == null){              
        installId = Math.random() * 10000000000;
      }
         
      let play_mode = "play";
      if (fromDemo){
        play_mode = "demo";
      } else if (props.fromMaker){
        play_mode = "preview";
      }
      else if (checkAuth && getAuth().currentUser != null){          
        play_mode = "review";
      }   
      setPlayMode(play_mode);        
           
      const clientId = (!checkAuth || getAuth().currentUser == null) ? installId : getAuth().currentUser.uid;

      if (play_mode === "play"){ // already set in the other modes
        analyticsManager.setUserId(clientId);            
        analyticsManager.setUserProperty(testedGroup, null);  
      }

      analyticsManager.logPlayStoryScreenViewTag(storyId, play_mode, window.location.href.includes("fbclid") ? "meta_share" : null);
                  
      appAnalyticsManager.current = new AppAnalyticsManager(storyId, story.preview || play_mode !== "play", story.uid, clientId);     
      appAnalyticsManager.current.init();        
      appAnalyticsManager.current.updateViewNb(); 
    }
      
  }, [story, storyId]);

  useEffect(() => {         
    fetchStory();
  }, []);

  useEffect(() => {
    if (appAnalyticsManager.current != null){
      updateVisitCounter();
    }

    async function updateVisitCounter(){
      setTimeout(() => {   
        appAnalyticsManager.current.visitDurationCounter();
        if (appAnalyticsManager.current.totalDuration < 30){
          updateVisitCounter();
        }
      }, 2000);
    }
    
  }, [appAnalyticsManager.current != null]);
  
  useEffect(() => {
    if (props.featureReset != null && props.featureReset){
      setActiveFeature(null);
      props.setFeatureReset(false);
    }
  }, [props.featureReset]);

  useEffect(() => {
    if (props.resetStickerOutOfScreen){
      setStickersOutOfScreen(false);
      props.setResetStickerOutOfScreen(false);
    }
  }, [props.resetStickerOutOfScreen]);

  useEffect(() => {
    if (props.highlight != null){
        setTimeout(() => {props.setHighlight(null)}, 2000);
    }
  }, [props.highlight]);

  useEffect(() => {

    function handleOrientationChange(e){      
      setTimeout(() => handleResize(false), 200);
    }

    if (! checkMobile()) return;

    if (window.screen != null && window.screen.orientation != null)
      window.screen.orientation.addEventListener("change", handleOrientationChange);

    return () => {      
      if (window.screen != null && window.screen.orientation != null)
        window.screen.orientation.removeEventListener('change', handleOrientationChange);
    };
    
  }, []);

  let touchToPlayControls = useRef(null); 
  let insideControls = useRef(null);
  let menuControls = useRef(null);

  function showInteractiveAnimation(){
    if (playInteractiveAnimation ){      
      return (
        <img id="touch-to-play" src="images/ic_touch_app.png" alt={t('TouchToPlay')} style={{width: 0, height: 0, position: 'absolute', transform: "translate(-50%, -50%)",top: '50%',left: '50%', cursor: "pointer"}} />
      );
    }
    else {
      if (touchToPlayControls.current != null || insideControls.current != null || menuControls.current != null){
        if (touchToPlayControls.current != null){
          touchToPlayControls.current.cancel();
          touchToPlayControls.current = null;
        }
        if (insideControls.current != null){
          insideControls.current.cancel();
          insideControls.current = null;
        }
        if (menuControls.current != null){
          menuControls.current.cancel();
          menuControls.current = null;
        }
        const touchToPlay = getElInPlayerContainer("touch-to-play");
        if (touchToPlay != null){
          touchToPlay.style.display = "none";
        }
        const insideSticker = getElInPlayerContainer("inside");
        if (insideSticker != null){
          insideSticker.style.opacity = 1;          
        }
        const menuSticker = getElInPlayerContainer("menu");
        if (menuSticker != null){
          menuSticker.style.opacity = 1;
        }
      }
      return null;
    }
  }

  useEffect(() => {    
    if (playInteractiveAnimation && story != null && storyDimensions != undefined){            
      setTimeout(() => playAnimation(), 5000);
    }
  }, [playInteractiveAnimation && story != null && storyDimensions != undefined]);

  function playAnimation(){
    const touchToPlay = getElInPlayerContainer("touch-to-play");
      const insideSticker = getElInPlayerContainer("inside");
      const menuSticker = getElInPlayerContainer("menu");

      if (touchToPlay != null && menuSticker != null && !storyClicked1Time){  
        // use of width over scale (scale does not work with translate centering)
        const touchSequence = [  
          [touchToPlay, {opacity: 0}, {duration: 0.1}],
          [touchToPlay, {width: 192, height: 192}, {duration: 0.1}],
          [touchToPlay, {opacity: 1}, {duration: 0.6}],     
          [touchToPlay, { width: 192, height: 192, }, {ease: "easeOut", duration: 0.6}],
          [touchToPlay, { width: 96, height: 96}, {ease: "easeIn", duration: 0.6}],
          [touchToPlay, { width: 192, height: 192}, {ease: "easeOut", duration: 0.6}],
          [touchToPlay, { width: 96, height: 96}, {ease: "easeIn", duration: 0.6}],
          [touchToPlay, { width: 192, height: 192}, {ease: "easeOut", duration: 0.6}],              
          [touchToPlay, {opacity: 0}, {duration: 0.1}],
        ];
        touchToPlayControls.current = animate(touchSequence);

        if (insideSticker != null){
          const insideSequence = [
            [insideSticker, {opacity: 0}, {duration: 0.6}], 
            [insideSticker, {scale: 1}, {duration: 3.5}], 
            [insideSticker, {opacity: 1}, {duration: 0.1}]
          ];
          insideControls.current = animate(insideSequence);
        }
        else {
          insideControls.current = null;
        }

        const menuSequence = [
          [menuSticker, {opacity: 0}, {duration: 0.6}],
          [menuSticker, {scale: 1}, {duration: 3.5}],
          [menuSticker, {opacity: 1}, {duration: 0.1}]
        ];
        menuControls.current = animate(menuSequence);    
        
        setTimeout(() => playAnimation(), 8000);
      }
  }

  if (story == null || storyDimensions == undefined){
    const isEmbeddedDemo = Boolean(fromDemo) || shouldDisablePlayerFullscreen();
    const loadingViewportWidth = windowDimensions?.width ?? props.windowDimensions?.width ?? 0;
    const loadingViewportHeight = windowDimensions?.height ?? props.windowDimensions?.height ?? 0;
    const exceedsCompactStoryViewport = loadingViewportWidth > 540 || loadingViewportHeight > 960;
    const useWhiteLoadingScreen = !isEmbeddedDemo && (
      props.fromMaker || playerOnDesktop || exceedsCompactStoryViewport
    );
    const loadingBgColor = useWhiteLoadingScreen ? '#FFFFFF' : '#000';
    const showLoadingSparkles = !props.fromMaker;
    return (
      <div
        className="App"
        id={playerContainerId}
        style={
          isEmbeddedDemo
            ? { backgroundColor: '#000', width: '100%', height: '100%', minHeight: '100%', overflow: 'hidden' }
            : useWhiteLoadingScreen
              ? { backgroundColor: '#FFFFFF' }
              : undefined
        }
      >
        <div
          className={fromDemo ? 'StoryVideoContainerPreview' : 'StoryVideoContainer'}
          style={{
            width: fromDemo && windowDimensions ? windowDimensions.width : '100%',
            height: fromDemo && windowDimensions ? windowDimensions.height : '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: loadingBgColor,
          }}
        >
          {showLoadingSparkles && <img src="/images/sparkles.gif" alt={t('SparklesAlt')} className="Sparkles" width="220px"/>}
        </div>
      </div>
    );
  }
  /*else if (demoStory){
    // sandbox to test components in isolation

    return (
      <>
        <div className="StoryVideoContainer" style={{width: storyDimensions.containerWidth, height: storyDimensions.containerHeight}}>
          <PlayerContext.Provider value={new Map([["windowDimensions", windowDimensions], ["storyDimensions", storyDimensions], ["sizeMultiplier", sizeMultiplier], ["propagateFocusChange", propagateFocusChange], ["setFullScreen", setFullScreen], ["pauseVideo", pauseVideo], ["activeFeature", activeFeature], ["setActiveFeature", requestActiveFeature], ["openChainRestaurantPicker", openChainRestaurantPicker], ["menuColor", story.menuColor], ["primaryColor", story.primaryColor], ["secondaryColor", story.secondaryColor], ["appAnalyticsManager", appAnalyticsManager.current], ["fromMaker", props.fromMaker], ["playMode", playMode], ["highlight", props.highlight], ["setHighlight", props.setHighlight]])}>
            
          </PlayerContext.Provider>
        </div>
      </>
    );
  }*/
 else {
  const showInfos = activeFeature == null;  
  const classVideo = props.fromMaker && ! isTabletOrMobile || fromDemo ? "StoryVideoContainerPreview" : "StoryVideoContainer";
  
    if (! playerOnDesktop){      
      return (
              <div className="App" id={playerContainerId}>  
                <img
                  src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%201%201'%20preserveAspectRatio='none'%3E%3C/svg%3E"
                  alt={t('SpacerAlt')}
                  width="100%"
                  height="100%"
                  style={{position: 'absolute'}}
                  onClick={()=>{}}
                />                
                <div onClick={()=>manageClickOnRoot()}>
                  {/*!fromDemo &&*/ story.musicUrl && <audio id={idPrefix + "audio-player"} ref={audioRef} preload="none" loop src={story.musicUrl} disableRemotePlayback/>}            
                  <PlayerContext.Provider value={new Map([["windowDimensions", windowDimensions], ["storyDimensions", storyDimensions], ["sizeMultiplier", sizeMultiplier], ["propagateFocusChange", propagateFocusChange], ["setFullScreen", setFullScreen], ["pauseVideo", pauseVideo], ["activeFeature", activeFeature], ["setActiveFeature", requestActiveFeature], ["openChainRestaurantPicker", openChainRestaurantPicker], ["menuColor", story.menuColor], ["primaryColor", story.primaryColor], ["secondaryColor", story.secondaryColor], ["appAnalyticsManager", appAnalyticsManager.current], ["fromMaker", props.fromMaker], ["playMode", playMode], ["highlight", props.highlight], ["setHighlight", props.setHighlight], ["fromDemo", fromDemo], ["playInteractiveAnimation", playInteractiveAnimation], ["setPlayInteractiveAnimation", setPlayInteractiveAnimation], ["stickerOpeningPlayed", stickerOpeningPlayed], ["setStickerOpeningPlayed", setStickerOpeningPlayed], ["playAudio", playAudio], ["resetAudio", props.resetAudio], ["setResetAudio", props.setResetAudio], ["setPlayAudio", setPlayAudio], ["audioAlreadyDisabled", audioAlreadyDisabled], ["setAudioAlreadyDisabled", setAudioAlreadyDisabled], ["playerOnDesktop", playerOnDesktop], ["menuStickerClicked", menuStickerClicked], ["setMenuStickerClicked", setMenuStickerClicked], ["insideStickerClicked", insideStickerClicked], ["setInsideStickerClicked", setInsideStickerClicked], ["setShareButtonAlreadyHighlighted", setShareButtonAlreadyHighlighted], ["shareButtonAlreadyHighlighted", shareButtonAlreadyHighlighted]])}>                  
                    <div className={classVideo} style={{width: storyDimensions.containerWidth, height: storyDimensions.containerHeight, backgroundColor: "black" }}>                
                        <SideMenu storyId={storyId} story={story} placeDetails={placeDetails}/>
                        <StoryVideo storyId={storyId} story={story} setVideoLoaded={props.setVideoLoaded} fromDemo={fromDemo} fromMaker={props.fromMaker}/>    
                        <StickerContext.Provider value={new Map([["activeDrag", activeDrag], ["setActiveDrag", setActiveDrag], ["setStickerDrop", props.setStickerDrop], ["menuStickerDimensions", menuStickerDimensions], ["setMenuStickerDimensions", setMenuStickerDimensions], ["setInsideStickerDimensions", setInsideStickerDimensions], ["insideStickerDimensions", insideStickerDimensions], ["snackSwipeAlreadyDisplayed", snackSwipeAlreadyDisplayed], ["setSnackSwipeAlreadyDisplayed", setSnackSwipeAlreadyDisplayed], ["stickersOutOfScreen", stickersOutOfScreen], ["setStickersOutOfScreen", setStickersOutOfScreen], ["stickersDraggable", props.stickersDraggable], ["menuTitleFont", menuTitleFont], ["setMenuTitleFont", setMenuTitleFont], ["menuTextFont", menuTextFont], ["menuDescFont", menuDescFont], ["menuTitleFontFallback", menuTitleFontFallback], ["menuTextFontFallback", menuTextFontFallback], ["menuDescFontFallback", menuDescFontFallback], ["menuFontsLoading", menuFontsLoading]])}>
                          <StickerInside selectedItem={mediaFullScreenOpened} placeDetails={placeDetails} storyId={storyId} story={story} setFullScreen={setFullScreen}/>                          
                          <StickerGallery selectedItem={mediaFullScreenOpened} storyId={storyId} story={story} placeDetails={placeDetails} mediasType={menuType} medias={story.mediasRef} setFullScreen={setFullScreen} fromMaker={props.fromMaker} setStoryPreviewCurrentScreen={props.setStoryPreviewCurrentScreen}/>                                                                                         
                        </StickerContext.Provider>                        
                        <RestaurantInfos show={showInfos} story={story} storyId={storyId} placeDetails={placeDetails}/>                                                  
                        {showInteractiveAnimation()}
                        <ChainRestaurantPlaceDialog opened={activeFeature === CHAIN_INTERMEDIATE_FEATURE} onClose={closeChainPlaceDialog} onPlaceSelected={handleChainPlaceSelected} story={story} storyId={storyId} />
                    </div>   
                    <FullScreenMediaPlayer isFullScreenOpened={isFullScreenOpened} mediaFullScreenOpened={mediaFullScreenOpened} fullScreenMediaFormat={fullScreenMediaFormat} setFullScreen={setFullScreen} btnColor={story.secondaryColor}/>                           
                  </PlayerContext.Provider>    
                </div>            
                {pixelConsentNeeded && <PixelConsentOverlay sizeMultiplier={sizeMultiplier} onAccept={acceptPixelConsent} onRefuse={refusePixelConsent} onClose={closePixelConsentOverlay} hasMeta={!!installablePixels.meta} hasTikTok={!!installablePixels.tiktok} isReopen={pixelConsentIsReopen} />}
              </div>
            );  
    }  
    else { 
      const containerHeight = windowDimensions.height + 40;
      const doublePhoneContainerHeight = windowDimensions.height + 20;
      const rightPhoneContainerHeight = windowDimensions.height + 32;
      
      const desktopLanguage = browserLocales(true)[0] === "fr" ? "fr" : "en";
      const displayedPosType = story.posType != null ? translatePlaceType(story.posType, desktopLanguage) : "";

      let typeAddressSeparator = "";
      if (getTextWidth(displayedPosType + " · " + story.posArea, "16px Roboto") > 372 - 2 * 16 ){        
        typeAddressSeparator = "\n";        
      }
      else if (displayedPosType.length > 0){
        typeAddressSeparator = " · ";
      }

      let address = story.posArea;
      if (story.posArea != null && story.posArea.length > 0){        
        address = story.posArea;
      }
      else {
        address = story.city;
      }


      return(        

        <div style={{position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)"}}>
          <div style={{width: 840, height: containerHeight, display: "flex", flexDirection: 'row', justifyContent: 'center'}}>

            <div style={{width: 380, height: doublePhoneContainerHeight, position: 'relative', borderRadius: 30, padding: 6, background: 'white'}}>              
              {/* should stay same as 1st else */}
              <div className="App" id={playerContainerId} onClick={()=>manageClickOnRoot()}>                  
              {/*!fromDemo &&*/ story.musicUrl && <audio id={idPrefix + "audio-player"} ref={audioRef} preload="none" loop src={story.musicUrl} disableRemotePlayback/>}
                <PlayerContext.Provider value={new Map([["windowDimensions", windowDimensions], ["storyDimensions", storyDimensions], ["sizeMultiplier", sizeMultiplier], ["propagateFocusChange", propagateFocusChange], ["setFullScreen", setFullScreen], ["pauseVideo", pauseVideo], ["activeFeature", activeFeature], ["setActiveFeature", requestActiveFeature], ["openChainRestaurantPicker", openChainRestaurantPicker], ["menuColor", story.menuColor], ["primaryColor", story.primaryColor], ["secondaryColor", story.secondaryColor], ["appAnalyticsManager", appAnalyticsManager.current], ["fromMaker", props.fromMaker], ["playMode", playMode], ["highlight", props.highlight], ["setHighlight", props.setHighlight], ["fromDemo", fromDemo], ["playInteractiveAnimation", playInteractiveAnimation], ["setPlayInteractiveAnimation", setPlayInteractiveAnimation], ["stickerOpeningPlayed", stickerOpeningPlayed], ["setStickerOpeningPlayed", setStickerOpeningPlayed],  ["playAudio", playAudio], ["resetAudio", props.resetAudio], ["setResetAudio", props.setResetAudio],["setPlayAudio", setPlayAudio], ["audioAlreadyDisabled", audioAlreadyDisabled], ["setAudioAlreadyDisabled", setAudioAlreadyDisabled], ["playerOnDesktop", playerOnDesktop], ["menuStickerClicked", menuStickerClicked], ["setMenuStickerClicked", setMenuStickerClicked], ["insideStickerClicked", insideStickerClicked], ["setInsideStickerClicked", setInsideStickerClicked], ["setShareButtonAlreadyHighlighted", setShareButtonAlreadyHighlighted], ["shareButtonAlreadyHighlighted", shareButtonAlreadyHighlighted]], )}>                                    
                  <div className={classVideo} style={{zIndex: 100, backgroundColor: "transparent",width: storyDimensions.containerWidth, height: storyDimensions.containerHeight, borderRadius: 10, borderColor: 'white', borderWidth: 4, borderStyle: 'solid', pointerEvents: 'none'}}/>
                  <div className={classVideo} style={{width: storyDimensions.containerWidth, height: storyDimensions.containerHeight, backgroundColor: "black" /* addition to tablet */ }}>                
                      <SideMenu storyId={storyId} story={story} placeDetails={placeDetails}/>
                      <StoryVideo storyId={storyId} story={story} setVideoLoaded={props.setVideoLoaded} fromDemo={fromDemo} fromMaker={props.fromMaker}/>    
                      <StickerContext.Provider value={new Map([["activeDrag", activeDrag], ["setActiveDrag", setActiveDrag], ["setStickerDrop", props.setStickerDrop], ["menuStickerDimensions", menuStickerDimensions], ["setMenuStickerDimensions", setMenuStickerDimensions], ["setInsideStickerDimensions", setInsideStickerDimensions], ["insideStickerDimensions", insideStickerDimensions], ["snackSwipeAlreadyDisplayed", snackSwipeAlreadyDisplayed], ["setSnackSwipeAlreadyDisplayed", setSnackSwipeAlreadyDisplayed], ["stickersOutOfScreen", stickersOutOfScreen], ["setStickersOutOfScreen", setStickersOutOfScreen], ["stickersDraggable", props.stickersDraggable], ["menuTitleFont", menuTitleFont], ["setMenuTitleFont", setMenuTitleFont], ["menuTextFont", menuTextFont], ["menuDescFont", menuDescFont], ["menuTitleFontFallback", menuTitleFontFallback], ["menuTextFontFallback", menuTextFontFallback], ["menuDescFontFallback", menuDescFontFallback], ["menuFontsLoading", menuFontsLoading], ])}>
                        <StickerInside selectedItem={mediaFullScreenOpened} placeDetails={placeDetails} storyId={storyId} story={story} setFullScreen={setFullScreen}/>                          
                        <StickerGallery selectedItem={mediaFullScreenOpened} storyId={storyId} story={story} placeDetails={placeDetails} mediasType={menuType} medias={story.mediasRef} setFullScreen={setFullScreen} fromMaker={props.fromMaker} setStoryPreviewCurrentScreen={props.setStoryPreviewCurrentScreen}/>                                                                                         
                      </StickerContext.Provider>                      
                      <RestaurantInfos show={showInfos} story={story} storyId={storyId} placeDetails={placeDetails}/>   
                      {showInteractiveAnimation()}
                      <ChainRestaurantPlaceDialog opened={activeFeature === CHAIN_INTERMEDIATE_FEATURE} onClose={closeChainPlaceDialog} onPlaceSelected={handleChainPlaceSelected} story={story} storyId={storyId} />
                  </div>  
                  <FullScreenMediaPlayer isFullScreenOpened={isFullScreenOpened} mediaFullScreenOpened={mediaFullScreenOpened} fullScreenMediaFormat={fullScreenMediaFormat} setFullScreen={setFullScreen} btnColor={story.secondaryColor}/>         
                </PlayerContext.Provider>                
              </div>
              {/* END */}
              
            </div>

            <div style={{width: 372, height: rightPhoneContainerHeight, marginLeft: 40, backgroundColor: 'white', borderRadius: 30, display: "flex", flexDirection: 'column', justifyContent: 'center'}}>
              <div style={{marginTop: -100}}>
                <div style={{width: '100%', display: "flex", flexDirection: 'column', alignItems: 'center'}}>
                  <div id="restaurant-infos-avatar" name="restaurant-infos-avatar">
                    <img alt={t('RestaurantAvatarAlt')} src={story.avatarUrl != null ? story.avatarUrl : "/images/avatar_placeholder.png"} className="FBAvatarContainer" draggable={false} style={{background: '#FFFFFF', width: 96, height: 96, userSelect: 'none', WebkitUserSelect: 'none', WebkitUserDrag: 'none'}} />
                  </div>
                  <div style={{marginTop: 10, fontSize: 20, paddingLeft: 16, paddingRight: 16, textAlign: 'center', fontWeight: "bold"}}>{story.posName != null ? (story.posName !== "Holberton School Paris" ? story.posName : "Holberton School") : ""}</div>
                  <div style={{marginTop: 10, fontSize: 16, paddingLeft: 16, paddingRight: 16, textAlign: 'center'}}>{typeAddressSeparator === "\n" ? <><br/></> : ""}{displayedPosType}{typeAddressSeparator === "\n" ? <><br/><br/></> : typeAddressSeparator}{address}</div>
                </div>
              </div>
            </div>

          </div>
          {pixelConsentNeeded && <PixelConsentOverlay sizeMultiplier={sizeMultiplier} onAccept={acceptPixelConsent} onRefuse={refusePixelConsent} onClose={closePixelConsentOverlay} hasMeta={!!installablePixels.meta} hasTikTok={!!installablePixels.tiktok} isReopen={pixelConsentIsReopen} />}
        </div>
      );
    }
  }

  function checkBgVideoPlaying(){ 
    if (!story) return; 
            
    if (document.getElementById(idPrefix + "bgVideo") != null){
      const video = document.getElementById(idPrefix + "bgVideo");
      if (!story.videoSyncWithMusic){
        video.play();
      }
      else {
        if (!isIOS() && video.muted && audioFromBgVideoOn)
          fadeInVolume(video, idPrefix);
        else 
          video.play();
      }
    }
    else if (document.getElementById(idPrefix + "bgVideoContainer") != null && document.getElementById(idPrefix + "bgVideoContainer").children[0] != null){
      const video = document.getElementById(idPrefix + "bgVideoContainer").children[0];
      if (!story.videoSyncWithMusic){
        video.play();
      }
      else {
        if (!isIOS() && video.muted && audioFromBgVideoOn)
          fadeInVolume(video, idPrefix);
        else 
          video.play();
      }
    }
  }  
  
  function pauseVideo(){         
    if (document.getElementById(idPrefix + "bgVideo") != null){      
      const video = document.getElementById(idPrefix + "bgVideo");
      if (!story.videoSyncWithMusic){        
        try {          
          video.pause();
        }
        catch (e){}
      }
      else {                
        if (!isIOS() && !video.muted && audioFromBgVideoOn)        
          fadeOutVolume(video);
        else
          try {
            video.pause();
          }
          catch (e){}
      }
    }
    else if (document.getElementById(idPrefix + "bgVideoContainer") != null && document.getElementById(idPrefix + "bgVideoContainer").children[0] != null){   
      const video = document.getElementById(idPrefix + "bgVideoContainer").children[0];
      if (!story.videoSyncWithMusic){
        try {
          video.pause();          
        }
        catch (e){}
      }
      else {            
        if (!isIOS() && !video.muted && audioFromBgVideoOn)    
          fadeOutVolume(video);
        else
          try {
            video.pause();
          }
          catch (e){}
      }
    }
  }
}

function requestAnimFrameCompat() {
  return window.requestAnimationFrame ||
         window.webkitRequestAnimationFrame ||
         window.mozRequestAnimationFrame ||
         window.oRequestAnimationFrame ||
         window.msRequestAnimationFrame ||
         function(callback) {
           // Fallback: utilise `setTimeout` avec un intervalle d'environ 16ms (pour 60fps)           
           return window.setTimeout(callback, 1000 / 60);
         };
};

export function fadeOutVolume(video, requestAnimeFrameFunction = requestAnimFrameCompat) {  
  const fadeStep = () => {
    if (video.volume > 0) {
      video.volume = Math.max(0, video.volume - 0.03);      
      try {
        requestAnimeFrameFunction()(fadeStep);      
      }
      catch (e){        
        video.muted = true;
        video.pause();
      }
    } else {      
      video.muted = true;
      video.pause();      
    }
  };
  fadeStep();
}

export function fadeInVolume(video, idPrefix, requestAnimeFrameFunction = requestAnimFrameCompat) {
  const fadeStep = () => {
    if (video.volume < 1) {
      video.volume = Math.min(1, video.volume + 0.03);
      try{
        requestAnimeFrameFunction()(fadeStep);            
      }
      catch (e){        
        video.muted = false;
        video.play();
        const volumeBtnOn = document.getElementById(idPrefix + "story-volume-btn-on");
        const volumeBtnOff = document.getElementById(idPrefix + "story-volume-btn-off");
        if (volumeBtnOn != null && volumeBtnOff != null){           
          volumeBtnOff.style.visibility = "visible";
          volumeBtnOn.style.visibility = "hidden";                    
        }
      }
    } else {
      video.muted = false;
      video.play();
      const volumeBtnOn = document.getElementById(idPrefix + "story-volume-btn-on");
      const volumeBtnOff = document.getElementById(idPrefix + "story-volume-btn-off");
      if (volumeBtnOn != null && volumeBtnOff != null){           
        volumeBtnOff.style.visibility = "visible";
        volumeBtnOn.style.visibility = "hidden";                    
      }
    }
  };
  fadeStep();
}

export function unmuteVideo(){ 
  if (document.getElementById("bgVideo") != null){
    document.getElementById("bgVideo").muted = false;
    //fadeInVolume(document.getElementById("bgVideo"), idPrefix);
  }
  else if (document.getElementById("bgVideoContainer") != null && document.getElementById("bgVideoContainer").children[0] != null){
    document.getElementById("bgVideoContainer").children[0].muted = false;
    //fadeInVolume(document.getElementById("bgVideoContainer").children[0], idPrefix);
  }
}

export function muteVideo(){ 
  if (document.getElementById("bgVideo") != null){
    document.getElementById("bgVideo").muted = true;
    //fadeOutVolume(document.getElementById("bgVideo"));
  }
  else if (document.getElementById("bgVideoContainer") != null && document.getElementById("bgVideoContainer").children[0] != null){         
    document.getElementById("bgVideoContainer").children[0].muted = true;
    //fadeOutVolume(document.getElementById("bgVideoContainer").children[0]);
  }
}

export function isVideoMuted(){ 
  if (document.getElementById("bgVideo") != null){
    return document.getElementById("bgVideo").muted;
  }
  else if (document.getElementById("bgVideoContainer") != null && document.getElementById("bgVideoContainer").children[0] != null){
    return document.getElementById("bgVideoContainer").children[0].muted;
  }
}

export function getBackgroundVideoElement(){ 
  if (document.getElementById("bgVideo") != null){
    return document.getElementById("bgVideo");
  }
  else if (document.getElementById("bgVideoContainer") != null && document.getElementById("bgVideoContainer").children[0] != null){
    return document.getElementById("bgVideoContainer").children[0];
  }
}

export default Player;
